import React from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import head from "lodash/head";

import { Instructor } from "@hubcapp/types";
import { areaActions, selectSelectedFranchise } from "@hubcapp/state";
import { sortPropositionAllocationsForTabs } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";
import { useQueryInstructorAreas } from "@hubcapp/hooks";

import { buildTabItems, getFranchiseTitle } from "./helpers";
import AreasCovered from "./AreasCovered";
import FeatureNotAvailable from "@hubcapp/views/FeatureNotAvailable";

const AreasCoveredStateProvider = () => {
  const selectedFranchise = useSelector(selectSelectedFranchise);
  const dispatch = useDispatch();
  const history = useHistory();
  const { trackEvent } = useTealium();
  const { instructor } = useAuth();

  if (instructor.status !== "Active") {
    return <FeatureNotAvailable />;
  }

  const onCompleted = (data: { instructor: Instructor }) => {
    if (!data?.instructor?.propositions) return;

    const franchises = sortPropositionAllocationsForTabs(
      data.instructor.propositions,
    );

    const initialFranchise = head(franchises);

    if (initialFranchise) {
      dispatch(areaActions.selectedFranchiseChanged(initialFranchise));
    }
  };

  const { data, loading } = useQueryInstructorAreas({
    variables: {
      instructorId: instructor.id,
    },
    onCompleted,
  });

  const handleChange = (_: unknown, franchiseId: string) => {
    const selectedFranchise = data?.instructor?.propositions?.find(
      prop => prop.id === franchiseId,
    );

    if (!selectedFranchise) return;

    dispatch(areaActions.selectedFranchiseChanged(selectedFranchise));

    trackEvent({
      eventCategory: "Areas & prices",
      eventAction: "Interactionn",
      eventLabel: `${getFranchiseTitle(selectedFranchise.proposition)} ${
        selectedFranchise.proposition.type
      }`,
    });
  };

  const handleNewAreaClick = () => {
    trackEvent({
      eventCategory: "Areas & prices",
      eventAction: "Button Click",
      eventLabel: "Add A New Area",
    });

    history.push(`/settings/areas-and-prices/add`);
  };

  const propositionAllocations = sortPropositionAllocationsForTabs(
    data?.instructor.propositions || [],
  );

  const areaTabItems = propositionAllocations
    ? buildTabItems(propositionAllocations)
    : undefined;

  return (
    <AreasCovered
      onTabChange={handleChange}
      franchise={selectedFranchise}
      handleNewAreaClick={handleNewAreaClick}
      loading={loading}
      filterTabItems={areaTabItems}
    />
  );
};

export default AreasCoveredStateProvider;
